import { useCallback, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { canAssignRole, canEditRole, hasAnyPermission, hasPermission, type Role } from '@solote/contracts';
import { useAuth } from './AuthProvider';
import { resolveHomeRoute } from './home-route';

export { ADMIN_ENTRY_PERMISSIONS, ADVERTISER_ROLES, resolveHomeRoute } from './home-route';
import { useConfig } from '@/core/config/ConfigProvider';

/**
 * AUTORIZAÇÃO NA INTERFACE
 *
 * O que este arquivo faz: decide o que aparece na tela.
 * O que este arquivo NÃO faz: proteger dados ou operações.
 *
 * A distinção é prática, não filosófica. Um usuário curioso abre o DevTools,
 * altera o estado do React e faz o botão de "excluir usuário" aparecer. Se o
 * endpoint aceitar a chamada, o dado se foi — a tela nunca foi a proteção.
 *
 * Por isso toda rota de admin abaixo tem um par no servidor, e o servidor
 * decide. Aqui a gente só evita mostrar caminho que a pessoa não pode
 * percorrer, que é uma questão de clareza.
 */

/** Permissões efetivas do usuário atual. Vazio = visitante. */
export function usePermissions(): readonly string[] {
  const { user } = useAuth();
  return user?.permissions ?? [];
}

/**
 * Para onde esta pessoa vai depois de entrar.
 *
 * Existe porque o destino era fixo em `/painel` — a área do ANUNCIANTE. Quem
 * é da equipe interna e não anuncia nada (moderador, financeiro) caía na
 * guarda daquela área e era devolvido para a home, sem explicação nenhuma.
 *
 * A ordem importa: quem acumula os dois papéis vai para o painel
 * administrativo, que é onde a pessoa trabalha.
 */
export function useHomeRoute(): string {
  const { user } = useAuth();
  return resolveHomeRoute(user);
}

/**
 * `can('listings.approve')` — nega por padrão.
 *
 * O padrão importa: com "permite salvo se negado", uma capacidade nova nasce
 * liberada para todo mundo até alguém lembrar de restringi-la. Com "nega
 * salvo se permitido", ela nasce fechada e alguém precisa abrir de propósito.
 */
export function useCan(): (permission: string | string[]) => boolean {
  const granted = usePermissions();

  return useCallback(
    (required: string | string[]) =>
      Array.isArray(required) ? hasAnyPermission(granted, required) : hasPermission(granted, required),
    [granted],
  );
}

/** Perfil do usuário e sua altura na hierarquia. */
export function useActorRole(): { role: Role | null; level: number } {
  const { user } = useAuth();
  const { access } = useConfig();

  return useMemo(() => {
    // Um usuário pode carregar mais de um papel. Vale o de maior nível — é
    // ele que define até onde a pessoa pode mexer na hierarquia.
    const role = access.roles
      .filter((candidate) => user?.roles.includes(candidate.key) ?? false)
      .sort((a, b) => b.level - a.level)[0];

    return { role: role ?? null, level: role?.level ?? 0 };
  }, [access.roles, user]);
}

/**
 * Esconde o conteúdo quando falta permissão.
 *
 * `fallback` existe para os casos em que sumir confunde mais que explicar —
 * uma coluna de tabela que some sem aviso parece defeito.
 */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const can = useCan();
  return <>{can(permission) ? children : fallback}</>;
}

/**
 * Guarda de rota por permissão.
 *
 * Responde 404 (rota inexistente) em vez de "acesso negado": confirmar que
 * `/admin/financeiro` existe já entrega o mapa do sistema para quem estava
 * chutando URLs.
 */
export function RequirePermission({ permission, children }: { permission: string | string[]; children: ReactNode }) {
  const { user, status } = useAuth();
  const can = useCan();
  const location = useLocation();

  if (status === 'loading') return null;

  if (!user) {
    return <Navigate to="/entrar" state={{ from: location.pathname }} replace />;
  }

  if (!can(permission)) {
    return <Navigate to="/nao-encontrado" replace />;
  }

  return <>{children}</>;
}

/**
 * Exige apenas CONTA — nenhuma permissão específica.
 *
 * Serve para telas que qualquer pessoa logada usa, mas que não fazem sentido
 * sem identidade. O checkout é o caso: a API de pagamentos amarra a cobrança
 * ao `sub` do token, então um visitante anônimo preencheria o formulário
 * inteiro para receber um 401 no clique de pagar.
 *
 * Manda para /entrar guardando o caminho, para a pessoa voltar aqui depois de
 * entrar em vez de cair na home.
 *
 * Como todo guard do cliente, isto é CONVENIÊNCIA. Quem recusa de verdade é a
 * API — e ela recusa mesmo que esta linha seja apagada.
 */
export function RequireAccount({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return null;

  if (!user) {
    return <Navigate to="/entrar" state={{ from: `${location.pathname}${location.search}` }} replace />;
  }

  return <>{children}</>;
}

/**
 * Regras de atribuição de perfil, prontas para a interface.
 *
 * Espelham exatamente o que o servidor vai refazer. A interface usa isso para
 * desabilitar a opção com um motivo legível, em vez de deixar a pessoa tentar
 * e tomar um erro sem explicação.
 */
export function useRoleAssignment() {
  const permissions = usePermissions();
  const { level } = useActorRole();

  return useMemo(() => {
    const actor = { permissions, roleLevel: level };

    return {
      canAssign: (role: Role) => canAssignRole(actor, role),
      canEdit: (role: Role) => canEditRole(actor, role),

      /** Por que não pode — para a interface conseguir explicar. */
      reasonToBlock: (role: Role): string | null => {
        if (role.level >= level) {
          return 'Você não pode atribuir um perfil do mesmo nível ou acima do seu.';
        }

        const missing = role.permissions.filter((candidate) => !hasPermission(permissions, candidate));
        if (missing.length > 0) {
          return `Este perfil inclui permissões que você não possui (${missing.slice(0, 3).join(', ')}${
            missing.length > 3 ? '…' : ''
          }).`;
        }

        return null;
      },
    };
  }, [permissions, level]);
}
